import { Box, Drawer, Typography, useTheme, Tabs, Tab, Button, IconButton } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import { useCreateVoice } from 'apis/business/agent/voice/hook';
import { IoClose } from 'react-icons/io5';
import { AiFillAudio } from 'react-icons/ai';
import { MdOutlineDone, MdOutlineFileUpload } from 'react-icons/md';
import { VscDebugRestart } from 'react-icons/vsc';
import { LuFileAudio, LuFileCheck2 } from 'react-icons/lu';
import { a11yProps } from 'components/Common/CustomTabPanel';
import VoiceDetailsForm from './VoiceDetailsForm';
import { VoiceDetailsFormValues } from './types';

interface AddCloneVoiceModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

export default function AddCloneVoiceModal({ isOpen, onClose }: AddCloneVoiceModalProps) {
  const theme = useTheme();
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isFinishButtonDisabled, setIsFinishButtonDisabled] = useState(true);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [uploadedAudioBlob, setUploadedAudioBlob] = useState<Blob | null>(null);
  const [value, setValue] = useState<number>(0);
  const [isRecordingStarted, setIsRecordingStarted] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const { mutate: createVoice, isPending: isCreatingVoice } = useCreateVoice();

  const { startRecording, stopRecording } = useReactMediaRecorder({
    audio: true,
    onStop: (blobUrl, blob) => {
      setAudioBlob(blob);
    }
  });

  useEffect(() => {
    return () => {
      stopVisualization();
    };
  }, []);

  const startVisualization = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;

      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      visualize();
    } catch (error) {
      console.error('Error accessing microphone for visualization:', error);
    }
  };

  const stopVisualization = () => {
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    }

    analyserRef.current = null;
    dataArrayRef.current = null;
  };

  const visualize = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context || !analyserRef.current || !dataArrayRef.current) {
      return;
    }

    const draw = () => {
      if (!analyserRef.current || !dataArrayRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArrayRef.current as any);
      context.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / dataArrayRef.current.length) * 2.5;
      let barHeight;
      let x = 0;

      for (const audioValue of dataArrayRef.current) {
        barHeight = audioValue;
        context.fillStyle = theme.palette.primary.main; // Color of bars
        context.fillRect(x, canvas.height - barHeight / 10, barWidth, barHeight / 2);
        x += barWidth + 1;
      }

      requestAnimationFrame(draw);
    };

    draw();
  };

  const startVoiceRecording = () => {
    setIsRecordingStarted(true);
    startRecording();
    startTimer();
    startVisualization();
  };

  const handleClose = () => {
    stopRecording();
    onClose();
    stopTimer();
    stopVisualization();
  };

  const resetRecording = () => {
    stopRecording();
    stopTimer();
    stopVisualization();
    setTimeElapsed(0);
    setIsFinishButtonDisabled(true);
    // Start fresh recording and visualization
    startRecording();
    startTimer();
    startVisualization();
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeElapsed((prev) => {
        if (prev + 1 === 30) {
          setIsFinishButtonDisabled(false);
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current!);
  };

  const handleFinish = () => {
    stopVoiceRecording();
    onClose();
    setIsSecondModalOpen(true);
  };

  const stopVoiceRecording = () => {
    stopRecording();
    stopTimer();
    stopVisualization();
  };

  const handleFileUpload = (data: VoiceDetailsFormValues) => {
    const currBlob = value === 0 ? uploadedAudioBlob : audioBlob;
    if (!currBlob) return;
    const formData = new FormData();
    formData.append('voice[audio]', currBlob, 'recording.wav');
    data.name && formData.append('voice[name]', data.name);
    data.language && formData.append('voice[language]', data.language);
    data.gender && formData.append('voice[gender]', data.gender);

    createVoice(formData);
    setIsSecondModalOpen(false);
  };

  const handleTabChange = (_: any, newValue: number) => {
    stopVoiceRecording();
    setTimeElapsed(0);
    setIsFinishButtonDisabled(true);
    setIsRecordingStarted(false);
    setValue(newValue);
  };

  return (
    <>
      <Drawer
        anchor='right'
        open={isOpen}
        onClose={() => {
          handleClose();
          resetRecording();
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400, md: 500, lg: 800 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 100001
          }
        }}
      >
        {/* Fixed Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #DEDEDE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <Typography variant='h6' fontWeight={500}>
            Clone Your Voice
          </Typography>
          <IconButton onClick={handleClose} size='small'>
            <IoClose size={24} />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          <Tabs
            sx={{ background: '#f8f9fa', p: 0.5, borderRadius: 2 }}
            value={value}
            onChange={handleTabChange}
            variant='fullWidth'
            aria-label='configure tabs'
          >
            <Tab
              label={
                <Box display='flex' alignItems='center' fontSize={20} gap={1}>
                  <MdOutlineFileUpload />
                  <Typography variant='body2'>Upload</Typography>
                </Box>
              }
              {...a11yProps('voiceClone', 'voiceClonePanel', 0)}
            />
            <Tab
              label={
                <Box display='flex' alignItems='center' fontSize={20} gap={1}>
                  <AiFillAudio />
                  <Typography variant='body2'>Record</Typography>
                </Box>
              }
              {...a11yProps('voiceClone', 'voiceClonePanel', 1)}
            />
          </Tabs>
          {value === 0 ? (
            <>
              <Box
                sx={{
                  border: '2px dashed #D3D3D3',
                  borderRadius: '8px',
                  p: 4,
                  textAlign: 'center',
                  mt: 3,
                  bgcolor: '#FAFAFA'
                }}
              >
                {uploadedAudioBlob ? (
                  <Box mt={3}>
                    <LuFileCheck2 size={48} color='#097969' />
                    <Typography variant='body1' mb={2} fontWeight={600}>
                      Audio File Added
                    </Typography>
                    <Button
                      variant='contained'
                      fullWidth
                      onClick={() => {
                        onClose();
                        setIsSecondModalOpen(true);
                      }}
                      disabled={!uploadedAudioBlob}
                    >
                      Submit
                    </Button>
                    <Button
                      variant='outlined'
                      fullWidth
                      sx={{ marginTop: 2 }}
                      onClick={() => setUploadedAudioBlob(null)}
                      disabled={!uploadedAudioBlob}
                    >
                      Reset
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <LuFileAudio size={48} color='#9E9E9E' />
                    <Typography variant='h6' fontWeight={500} mt={2}>
                      Drag and drop your audio file
                    </Typography>
                    <Typography variant='body2' color='text.secondary' mb={2}>
                      or click to browse
                    </Typography>
                    <Button variant='outlined' startIcon={<MdOutlineFileUpload />} component='label'>
                      Select file
                      <input
                        type='file'
                        hidden
                        accept='audio/mp3,audio/wav'
                        onChange={(e) => setUploadedAudioBlob(e.target.files?.[0] || null)}
                      />
                    </Button>
                  </Box>
                )}
              </Box>
              <Box textAlign='left' mt={4}>
                <Typography variant='subtitle1' fontWeight={600}>
                  Guidelines for best results:
                </Typography>
                <ul style={{ paddingLeft: '1.25rem', marginTop: 8, color: '#555', fontSize: '14px' }}>
                  <li>
                    <strong>Accepted formats:</strong> MP3 (preferred) or WAV
                  </li>
                  <li>
                    <strong>Recording length:</strong> At least 1 minute of audio, but avoid more than 3 minutes
                  </li>
                  <li>
                    <strong>Quality matters:</strong> Clear audio without reverb, artifacts, or background noise
                  </li>
                  <li>
                    <strong>Audio codec:</strong> MP3 at 128 kbps and above is recommended
                  </li>
                </ul>
                <Typography variant='caption' display='block' color='text.secondary' mt={1}>
                  <em>
                    How the audio was recorded is more important than the total length. The number of samples doesn't
                    matter, it's the total combined runtime that counts.
                  </em>
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box border='1px solid #ececec' px={2} py={3} borderRadius={2} mt={3}>
                <Typography variant='h6' fontWeight={500} align='center' gutterBottom>
                  Talk about your favorite city.
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <canvas ref={canvasRef} height='70' width='600' style={{ marginBottom: '16px' }} />
                </Box>

                <Typography variant='body1' align='center' gutterBottom>
                  <span style={{ color: 'red' }}>{`0:${timeElapsed < 10 ? '0' : ''}${timeElapsed} / 0:30`}</span>
                </Typography>
                {isRecordingStarted ? (
                  <Box display='flex' gap={2}>
                    <Button variant='outlined' fullWidth onClick={resetRecording} startIcon={<VscDebugRestart />}>
                      Restart
                    </Button>
                    <Button
                      variant='contained'
                      fullWidth
                      onClick={handleFinish}
                      disabled={isFinishButtonDisabled}
                      startIcon={<MdOutlineDone />}
                    >
                      Finish
                    </Button>
                  </Box>
                ) : (
                  <Button fullWidth onClick={() => startVoiceRecording()}>
                    Start Recording
                  </Button>
                )}
              </Box>

              <Typography fontSize={12} align='center' my={1} color='#09090a'>
                Or Read along
              </Typography>
              <Typography className='reading-paragraph' variant='body2' color='#777'>
                There's something about traveling that opens your eyes to the beauty of the world. Take my trip to the
                Amazon Rainforest, for example. Being there, amidst such vibrant nature, it's an indescribable feeling.
                It makes you wonder—how much of the world do we take for granted? Every trip, every new landscape,
                changes you a little, doesn't it? It makes you appreciate the planet more. And it's not just about
                seeing new places; it's about the perspectives you gain. It makes you want to explore even further.
              </Typography>
            </>
          )}
        </Box>
      </Drawer>

      <VoiceDetailsForm
        open={isSecondModalOpen}
        onClose={() => setIsSecondModalOpen(false)}
        onSubmit={handleFileUpload}
        isPending={isCreatingVoice}
      />
    </>
  );
}
